import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Typography,
  CircularProgress,
  Box,
  Collapse,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { partialRewriteParagraph } from '../../services/api';
import { useSnackbar } from 'notistack';
import ReactMarkdown from 'react-markdown';

const MAX_PREVIEW_HEIGHT = 200;

const PartialRewriteDialog = ({
  open,
  onClose,
  paragraph,
  articleId,
  selectedText,
  onSave,
  loading: externalLoading,
  models,
  settings,
  selectedModel,
  setSelectedModel,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [instructions, setInstructions] = useState('');
  const [isRewriting, setIsRewriting] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const contentRef = useRef(null);
  const [needsExpand, setNeedsExpand] = useState(false);
  const observerRef = useRef(null);

  useEffect(() => {
    // Reset expanded state when dialog opens/closes
    if (!open) {
      setExpanded(false);
    }
  }, [open]);

  useEffect(() => {
    const checkHeight = () => {
      if (contentRef.current) {
        const scrollHeight = contentRef.current.scrollHeight;
        console.log('Content height:', scrollHeight); // Debug log
        setNeedsExpand(scrollHeight > MAX_PREVIEW_HEIGHT);
      }
    };

    // Create ResizeObserver
    observerRef.current = new ResizeObserver(checkHeight);

    if (contentRef.current) {
      observerRef.current.observe(contentRef.current);
      checkHeight(); // Initial check
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [selectedText, open]);

  const handleRewrite = async () => {
    try {
      if (!instructions.trim()) {
        enqueueSnackbar('Please provide rewrite instructions.', { variant: 'warning' });
        return;
      }

      setIsRewriting(true);

      const response = await partialRewriteParagraph(articleId, paragraph.id, {
        selected_text: selectedText,
        instructions,
        model: selectedModel,
      });

      if (response.coins_used) {
        enqueueSnackbar(`-${response.coins_used.toFixed(2)} coins used`, { variant: 'info' });
      }

      onSave(response.content);
      onClose();
      setInstructions('');
    } catch (error) {
      enqueueSnackbar(error.message || 'Error rewriting selected text', { variant: 'error' });
    } finally {
      setIsRewriting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Rewrite Selected Text</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Box>
            <Typography variant="body1" gutterBottom>
              <strong>Selected Text:</strong>
            </Typography>
            <Box 
              sx={{ 
                bgcolor: 'grey.800', 
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'grey.700',
                position: 'relative',
                color: 'white',
              }}
            >
              <Box 
                ref={contentRef}
                sx={{ 
                  p: 2,
                  maxHeight: expanded ? 'none' : `${MAX_PREVIEW_HEIGHT}px`,
                  overflow: 'hidden',
                  transition: 'max-height 0.3s ease-in-out',
                  position: 'relative',
                  '& > *:first-child': { mt: 0 },
                  '& > *:last-child': { mb: needsExpand && !expanded ? 3 : 0 },
                }}
              >
                <ReactMarkdown>{selectedText}</ReactMarkdown>
              </Box>
              {needsExpand && (
                <Box
                  onClick={() => setExpanded(!expanded)}
                  sx={{
                    position: expanded ? 'relative' : 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    py: 1,
                    cursor: 'pointer',
                    color: 'white',
                    bgcolor: expanded ? 'grey.800' : 'rgba(33, 33, 33, 0.95)',
                    borderTop: expanded ? '1px solid' : 'none',
                    borderColor: 'grey.700',
                    background: expanded ? 'none' : 'linear-gradient(180deg, rgba(33, 33, 33, 0) 0%, rgba(33, 33, 33, 0.95) 15%, rgba(33, 33, 33, 0.95) 100%)',
                    '&:hover': {
                      bgcolor: expanded ? 'grey.700' : 'rgba(66, 66, 66, 0.98)',
                    },
                  }}
                >
                  {expanded ? (
                    <>
                      <KeyboardArrowUpIcon sx={{ mr: 1 }} />
                      <Typography variant="button">Show Less</Typography>
                    </>
                  ) : (
                    <>
                      <KeyboardArrowDownIcon sx={{ mr: 1 }} />
                      <Typography variant="button">Show More</Typography>
                    </>
                  )}
                </Box>
              )}
            </Box>
          </Box>
          <TextField
            label="Rewrite Instructions"
            multiline
            rows={4}
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            placeholder="Describe how you want the selected text to be rewritten..."
            fullWidth
          />
          <FormControl fullWidth>
            <InputLabel>Model</InputLabel>
            <Select
              value={selectedModel}
              label="Model"
              onChange={(e) => setSelectedModel(e.target.value)}
              disabled={externalLoading}
            >
              {models.map((model) => (
                <MenuItem 
                  key={model.name} 
                  value={model.name}
                  sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}
                >
                  <Typography>{model.name} ({model.provider})</Typography>
                  {model.name === settings?.default_paragraph_writer_model && (
                    <Typography variant="caption" color="primary.main" sx={{ fontStyle: 'italic' }}>
                      (Default)
                    </Typography>
                  )}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleRewrite}
          variant="contained"
          disabled={isRewriting || externalLoading || !instructions.trim()}
          startIcon={isRewriting ? <CircularProgress size={20} /> : null}
        >
          Rewrite
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PartialRewriteDialog; 